/**
 * locale.ts — какой язык у КАБИНЕТА (WEB-1874, этап 2).
 *
 * ⚠ Не путать с языком ВОРОНКИ. Язык воронки живёт в её собственных данных
 * (`editorSlice.language`, `utils/publicQuizStrings.ts`) и правит публичной
 * страницей: там надписи обязаны быть на языке покупателя, а не автора.
 * Здесь — только язык интерфейса автора: меню, кнопки, тосты, ошибки.
 *
 * ПРАВДА О ЯЗЫКЕ — поле профиля `users.language` (колонка есть с самого
 * начала: backend/src/users/entities/user.entity.ts, умолчание `en`).
 * Переключатель стоит в Настройках → Персонализация и уже пишет туда PATCH
 * профиля. Второго органа управления (кнопки в шапке) намеренно нет.
 *
 * До входа профиля нет, а экраны входа/регистрации/сброса переводить надо:
 *   1) запомненный выбор на этом устройстве (localStorage `w2a.uiLocale` —
 *      его пишет App сразу, как узнал язык профиля);
 *   2) язык браузера, если основной тег ru или en;
 *   3) `en` — то же умолчание, что у колонки базы, у
 *      `backend/src/auth/utils/resolve-language.ts` и у
 *      `resolvePublicQuizLocale` в плеере. Одно умолчание на весь продукт;
 *      три места не должны расходиться (решение владельца 04.09).
 */
import { useCallback, useContext, useSyncExternalStore } from 'react';
import { ReactReduxContext } from 'react-redux';

export type UiLocale = 'en' | 'ru';

/** Поддерживаемые языки кабинета. Порядок = порядок в переключателе. */
export const UI_LOCALES: readonly UiLocale[] = ['en', 'ru'];

/** Ключ запомненного выбора. Читается ДО ответа `/auth/me`, чтобы не мигать. */
export const UI_LOCALE_STORAGE_KEY = 'w2a.uiLocale';

/** Умолчание продукта — см. докблок. */
export const DEFAULT_UI_LOCALE: UiLocale = 'en';

/** `ru-RU` → `ru`; всё, чего мы не поддерживаем, → null (а не «наугад»). */
function normalizeTag(tag: string | null | undefined): UiLocale | null {
  if (typeof tag !== 'string') return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  return (UI_LOCALES as readonly string[]).includes(primary)
    ? (primary as UiLocale)
    : null;
}

/**
 * Хранилище может быть недоступно целиком (приватный режим Safari, «блокировать
 * все cookie», встроенные браузеры) — тогда доступ бросает, а не отдаёт null.
 * Та же дисциплина try/catch, что в `utils/lazyWithRetry.ts`.
 */
function readStoredLocale(): UiLocale | null {
  try {
    return normalizeTag(window.localStorage.getItem(UI_LOCALE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function browserLocale(): UiLocale | null {
  try {
    const list =
      typeof navigator !== 'undefined' && Array.isArray(navigator.languages)
        ? navigator.languages
        : [typeof navigator !== 'undefined' ? navigator.language : ''];
    for (const tag of list) {
      const known = normalizeTag(tag);
      if (known) return known;
    }
  } catch {
    // Нет navigator (не браузерное окружение) — падаем в умолчание ниже.
  }
  return null;
}

/**
 * Язык кабинета по всей цепочке источников.
 * @param profileLanguage значение `users.language`, если человек уже вошёл.
 */
export function resolveUiLocale(profileLanguage?: string | null): UiLocale {
  return (
    normalizeTag(profileLanguage) ??
    readStoredLocale() ??
    browserLocale() ??
    DEFAULT_UI_LOCALE
  );
}

/** Запомнить язык на устройстве — чтобы экран входа сразу был правильным. */
export function rememberUiLocale(locale: UiLocale): void {
  try {
    window.localStorage.setItem(UI_LOCALE_STORAGE_KEY, locale);
  } catch {
    // Хранилище заблокировано — просто без ускорения следующей загрузки.
  }
}

/**
 * Текущий язык для кода ВНЕ React: thunk'и, утилиты, `message.*` из сервисов.
 * Держится в актуальном состоянии из `App.tsx` (см. setCurrentUiLocale) —
 * иначе тост из thunk'а говорил бы не на том языке, что экран рядом.
 */
let currentUiLocale: UiLocale = resolveUiLocale();

export function setCurrentUiLocale(locale: UiLocale): void {
  currentUiLocale = locale;
}

export function getUiLocale(): UiLocale {
  return currentUiLocale;
}

/**
 * Язык кабинета в компоненте.
 *
 * ЧИТАЕТ СОСТОЯНИЕ НАПРЯМУЮ, а не через типизированные хуки `@/hooks/hooks`:
 * те тянут за собой модуль стора, а словари нужны и самому стору (тексты
 * отказов в thunk'ах) — получился бы цикл импортов. Форма состояния описана
 * здесь минимально, ровно тем куском, который читается.
 *
 * И НЕ ПАДАЕТ БЕЗ ПРОВАЙДЕРА. Это не мелочь: словарь зовут почти все
 * компоненты кабинета, а рисуются они не только внутри дерева со стором —
 * экран падения (`AppErrorBoundary`), витрина компонентов и добрая половина
 * существующих тестов рендерят лист без `<Provider>`. Обычный `useSelector`
 * там бросает «could not find react-redux context value» — то есть механизм
 * локализации ронял бы интерфейс ровно там, где и без него плохо. Поэтому
 * стор берётся из контекста осторожно, и без него отвечаем текущей локалью
 * (той же, что получает код вне React) — тост и экран под ним не разойдутся.
 *
 * Все хуки вызываются безусловно; ветвится только содержимое подписки.
 */
type AuthLanguageSlice = {
  readonly auth?: { readonly user?: { readonly language?: string | null } | null };
};

/**
 * Язык кабинета ВМЕСТЕ с признаком, откуда он взят: из профиля человека
 * (`users.language`) или угадан (запомненный выбор → язык браузера → `en`).
 *
 * Признак нужен ровно одному месту — записи в localStorage (`App.tsx`).
 * Запоминать догадку нельзя: запомненное читается РАНЬШЕ языка браузера, и
 * один раз записанная догадка навсегда перебила бы браузер, а на общем
 * устройстве досталась бы следующему гостю (ревизия пачки 15, R1-14).
 */
export function useUiLocaleSource(): { locale: UiLocale; fromProfile: boolean } {
  const context = useContext(ReactReduxContext);
  const store = context?.store ?? null;

  const subscribe = useCallback(
    (onChange: () => void) => (store ? store.subscribe(onChange) : () => {}),
    [store],
  );
  const readLanguage = useCallback(() => {
    if (!store) return null;
    const state = store.getState() as AuthLanguageSlice;
    return state.auth?.user?.language ?? null;
  }, [store]);

  // Снимок — примитив (строка или null), поэтому лишних перерисовок нет и
  // третий аргумент (серверный снимок) безопасно тот же.
  const language = useSyncExternalStore(subscribe, readLanguage, readLanguage);

  // Язык профиля, которого мы не поддерживаем (или пустой), — тоже НЕ правда о
  // выборе человека: показываем догадку и запоминать её не станем.
  const fromProfile = normalizeTag(language) !== null;
  return {
    locale: fromProfile ? resolveUiLocale(language) : getUiLocale(),
    fromProfile,
  };
}

/** Язык кабинета в компоненте (см. `useUiLocaleSource`). */
export function useUiLocale(): UiLocale {
  return useUiLocaleSource().locale;
}
